/**
 * Pagination helpers for `hasNext`-based pagination (no exact total available).
 *
 * The backend returns `{ items, page, limit, hasNext }` and intentionally does
 * NOT return a total count (computing COUNT(*) over millions of rows is too
 * expensive). Because of that we cannot know the last page number, so the
 * visible page window is a fixed-size "block" derived purely from the current
 * page. Forward navigation past the real end is prevented by `hasNext`.
 */

/**
 * Compute the visible window of page numbers for a fixed-size block pager.
 *
 * The window is a static block of `windowSize` pages:
 *   - pages 1..5   -> [1, 2, 3, 4, 5]
 *   - pages 6..10  -> [6, 7, 8, 9, 10]
 *   - pages 11..15 -> [11, 12, 13, 14, 15]
 *
 * @param page       Current 1-based page.
 * @param windowSize Maximum number of page buttons to show (default 5).
 * @returns Array of 1-based page numbers to render as buttons.
 */
export function getVisiblePageWindow(page: number, windowSize = 5): number[] {
  const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
  const safeSize = Number.isFinite(windowSize) && windowSize >= 1 ? Math.floor(windowSize) : 1;

  const blockStart = Math.floor((safePage - 1) / safeSize) * safeSize + 1;

  return Array.from({ length: safeSize }, (_, i) => blockStart + i);
}
